import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Card, ErrorBox, MiniButton, SectionHeader, SectionSubtitle, SectionTitle, StatusTag } from '../selfUi';

// What the organizers proposed with an invitation (GET /api/me/change-sets), one row per field:
// the value the profile holds today next to the proposed one, and a decision for each - the same
// "old vs new" reading as FieldDiffPicker.js, which is where this look comes from. A settled field
// (APPLIED / REJECTED, one of the set's other fields is still waiting) shows what it ended up as.
//
// Labels come from the same keys FieldDiffPicker uses, on this API's own key names (linkedinUrl,
// bioEn/bioDe); a key nobody has a text for falls back to its JSON name, which is what the backend
// would print anyway.
const FIELD_LABELS = {
  title: ['speaker.field.title', 'Title'],
  firstName: ['form.firstName', 'First Name'],
  lastName: ['form.lastName', 'Last Name'],
  titleSuffix: ['self.profile.field.titleSuffix', 'Name suffix'],
  phone: ['form.phoneNumber', 'Phone Number'],
  streetAddress: ['form.streetAddress', 'Street Address'],
  postalCode: ['form.postalCode', 'Postal Code'],
  city: ['form.city', 'City'],
  state: ['form.state', 'State/Province'],
  country: ['form.country', 'Country'],
  institution: ['speaker.field.institution', 'Institution / practice / clinic'],
  specialty: ['speaker.field.specialty', 'Specialty'],
  bioDe: ['speaker.field.shortBioDe', 'Short bio (German)'],
  bioEn: ['speaker.field.shortBioEn', 'Short bio (English)'],
  motivationDe: ['speaker.field.motivationShortDe', 'Motivation (German)'],
  motivationEn: ['speaker.field.motivationShortEn', 'Motivation (English)'],
  photoUrl: ['speaker.field.photoUrl', 'Photo URL'],
  linkedinUrl: ['speaker.field.linkedin', 'LinkedIn'],
  websiteUrl: ['speaker.field.website', 'Website'],
  instagramUrl: ['speaker.field.instagram', 'Instagram'],
  facebookUrl: ['speaker.field.facebook', 'Facebook / Meta'],
  professionalStatuses: ['speakerMgmt.statusCsv', 'Profile classification'],
  interests: ['speakerMgmt.interestsCsv', 'Cardiac interests'],
};

// FieldDiffPicker.js's own styled-components (Wrap/Row/RowLabel/Options/Option/OptionHead/
// OptionValue), unchanged but for the decision buttons under the two values.
const Wrap = styled.div`display:flex;flex-direction:column;gap:.6rem;`;
const Row = styled.div`border:1px solid #e2e8f0;padding:.7rem .85rem;background:#fff;`;
const RowLabel = styled.div`font-weight:700;font-size:.85rem;color:#334155;margin-bottom:.45rem;`;
const Options = styled.div`display:flex;gap:.6rem;flex-wrap:wrap;`;
const Option = styled.div`flex:1;min-width:12rem;display:flex;flex-direction:column;gap:.3rem;padding:.55rem .65rem;border:1px solid ${p => (p.$proposed ? '#47ABDB' : '#e2e8f0')};background:${p => (p.$proposed ? '#eff9fe' : '#f8fafc')};font-size:.8rem;`;
const OptionHead = styled.div`display:flex;align-items:center;gap:.4rem;font-weight:700;color:#0f172a;`;
const OptionValue = styled.div`color:#334155;white-space:pre-wrap;word-break:break-word;font-weight:400;padding-left:1.3rem;`;
const ValueList = styled.ul`margin:0;padding-left:2.1rem;color:#334155;font-size:.8rem;`;
const RowActions = styled.div`display:flex;gap:.55rem;flex-wrap:wrap;margin-top:.6rem;`;

const ProposedChanges = ({ changeSets, busyField, error, onDecide }) => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);

  const fields = (changeSets || []).flatMap((set) => set.fields || []);
  // Nothing proposed: the section stays away altogether (the hero's own banner only appears when
  // there is something to decide here).
  if (fields.length === 0) return null;

  const labelOf = (field) => (FIELD_LABELS[field] ? t(FIELD_LABELS[field][0], FIELD_LABELS[field][1]) : field);
  // A list field (interests, professionalStatuses) is stored as codes/labels - shown as a readable
  // list, translated the same way the edit form and InterestPicker translate them.
  const valueOf = (field, value) => {
    if (!Array.isArray(value) || value.length === 0) return <OptionValue>{value || '—'}</OptionValue>;
    const prefix = field === 'professionalStatuses' ? 'speaker.role.' : 'speaker.interest.';
    return <ValueList>{value.map((item) => <li key={item}>{t(`${prefix}${item}`, item)}</li>)}</ValueList>;
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>{t('self.profile.changes.title', 'Proposed changes')}</SectionTitle>
        <SectionSubtitle>{t('self.profile.changes.subtitle', 'Sent with your invitation: accept or reject each field. Nothing changes until you decide.')}</SectionSubtitle>
      </SectionHeader>
      <Card>
        <Wrap>
          {fields.map((field) => {
            const label = labelOf(field.field);
            if (field.state !== 'WAITING') {
              return (
                <Row key={field.id}>
                  <RowLabel>{label}</RowLabel>
                  {valueOf(field.field, field.state === 'APPLIED' ? field.proposed : field.current)}
                  <StatusTag $status={field.state === 'APPLIED' ? 'ACCEPTED' : 'REJECTED'}>
                    {field.state === 'APPLIED'
                      ? t('self.profile.fieldApplied', 'Applied to your profile')
                      : t('profile.status.REJECTED', 'Rejected')}
                  </StatusTag>
                </Row>
              );
            }
            return (
              <Row key={field.id}>
                <RowLabel>{label}</RowLabel>
                <Options>
                  <Option>
                    <OptionHead>{t('speakerMgmt.diff.keepCurrent', 'Keep current')}</OptionHead>
                    {valueOf(field.field, field.current)}
                  </Option>
                  <Option $proposed>
                    <OptionHead>{t('speakerMgmt.diff.useNew', 'Use new value')}</OptionHead>
                    {valueOf(field.field, field.proposed)}
                  </Option>
                </Options>
                <RowActions>
                  <MiniButton
                    type="button" disabled={busyField === field.id}
                    aria-label={t('self.profile.acceptField', 'Accept the proposed {field}', { field: label })}
                    onClick={() => onDecide(field.id, 'accept')}
                  >
                    {t('self.profile.accept', 'Accept')}
                  </MiniButton>
                  <MiniButton
                    $ghost type="button" disabled={busyField === field.id}
                    aria-label={t('self.profile.rejectField', 'Keep the current {field}', { field: label })}
                    onClick={() => onDecide(field.id, 'reject')}
                  >
                    {t('self.profile.reject', 'Reject')}
                  </MiniButton>
                </RowActions>
              </Row>
            );
          })}
        </Wrap>
      </Card>
      {error && <ErrorBox>{error}</ErrorBox>}
    </>
  );
};

export default ProposedChanges;
